import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import type { IPlace } from "@bbj-types/address";
import { yupResolver } from "@hookform/resolvers/yup";
import { AutocompletePlaces } from "@inputs/autocomplete/AutocompletePlacesUniq";
import { TextFieldForm } from "@inputs/hook-form/TextFieldForm";
import { PhoneInputV2 } from "@inputs/text/PhoneInputV2";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContactsIcon from "@mui/icons-material/ContactsOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import {
  Autocomplete,
  CircularProgress,
  Fab,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  type ContactResolver,
  EMAIL_OR_PHONE_ERROR_MESSAGE,
  type contactSchema,
  getContactSchema,
} from "@root/utils/resolvers/contact";
import { PaperCollapse } from "@surfaces/PaperCollapse";
import { useEffect, useState } from "react";
import {
  Controller,
  type FieldValues,
  type Path,
  type PathValue,
  useController,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";
import type { IFormGenInputProps } from "../index";

export type TContactValue = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  job?: string;
  email?: string;
  phone?: string;
  place?: IPlace;
  description?: string;
};

interface IContactModules {
  existingContacts: boolean;
  place: boolean;
  comment: boolean;
}

const pickContactFields = (contact: TContactValue, modules: IContactModules): TContactValue => ({
  ...(modules.existingContacts && contact._id && { _id: contact._id }),
  firstName: contact.firstName,
  lastName: contact.lastName,
  job: contact.job,
  email: contact.email,
  phone: contact.phone,
  ...(modules.place && { place: contact.place }),
  ...(modules.comment && { description: contact.description }),
});

const getContactLabel = (contact: TContactValue) =>
  [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || contact.phone || "Contact";

interface IContactFormProps {
  place: boolean;
  comment: boolean;
  defaultValues: TContactValue;
  validateOnMount?: boolean;
  onConfirm: (data: ContactResolver) => void;
  onCancel: () => void;
}

const ContactForm = ({ place, comment, defaultValues, validateOnMount, onConfirm, onCancel }: IContactFormProps) => {
  const {
    control: contactControl,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<ContactResolver>({
    resolver: yupResolver(getContactSchema(place) as typeof contactSchema),
    defaultValues,
    mode: validateOnMount ? "onChange" : "onSubmit",
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger validation once, when opened because of an invalid submitted contact
  useEffect(() => {
    if (validateOnMount) {
      void trigger();
    }
  }, []);

  const onConfirmCreate = handleSubmit(onConfirm);

  return (
    <Stack component="form" onSubmit={onConfirmCreate} sx={{ gap: 1 }}>
      <TextFieldForm control={contactControl} name="firstName" label="Prénom" size="small" fullWidth />
      <TextFieldForm control={contactControl} name="lastName" label="Nom" size="small" fullWidth />
      <TextFieldForm control={contactControl} name="job" label="Poste" size="small" fullWidth />
      <TextFieldForm
        control={contactControl}
        name="email"
        label="Email"
        size="small"
        fullWidth
        hideHelperText={errors.email?.type === "email-or-phone"}
      />
      <Controller
        control={contactControl}
        name="phone"
        render={({ field, fieldState }) => (
          <PhoneInputV2
            label="Téléphone"
            value={field.value}
            onChange={field.onChange}
            size="small"
            fullWidth
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.type === "email-or-phone" ? undefined : fieldState.error?.message}
          />
        )}
      />
      {(errors.email?.type === "email-or-phone" || errors.phone?.type === "email-or-phone") && (
        <FormHelperText sx={{ textAlign: "center" }} error required>
          {EMAIL_OR_PHONE_ERROR_MESSAGE}
        </FormHelperText>
      )}
      {place && (
        <Controller
          control={contactControl}
          name="place"
          render={({ field: placeField }) => (
            <AutocompletePlaces
              value={placeField.value ?? null}
              onChange={(_, value) => placeField.onChange(value ?? undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zone géographique *"
                  size="small"
                  fullWidth
                  error={Boolean(errors.place)}
                  helperText={errors.place?.message}
                />
              )}
            />
          )}
        />
      )}
      {comment && (
        <>
          <Typography component="p" variant="caption" sx={{ pt: 1 }}>
            Donnez le maximum d’informations sur ce contact : quelle est la nature de la relation ? comment transmettre
            les candidatures ? etc.
          </Typography>
          <TextFieldForm
            control={contactControl}
            name="description"
            label="Commentaires"
            multiline
            rows={3}
            size="small"
            fullWidth
          />
        </>
      )}
      <Typography component="p" variant="caption">
        Pour un contact, il faut a minima un email ou un téléphone.
      </Typography>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "end", gap: 1 }}>
        <Fab color="white" size="small" type="button" onClick={onCancel}>
          <CloseIcon sx={(theme) => ({ color: theme.palette.primary.dark })} />
        </Fab>
        <Fab color="white" size="small" type="submit">
          <CheckIcon sx={(theme) => ({ color: theme.palette.success.main })} />
        </Fab>
      </Stack>
    </Stack>
  );
};

export const FormGenInputContact = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  jsonSchemaElement,
  context,
}: IFormGenInputProps<Schema, T>) => {
  const modules: IContactModules = {
    existingContacts: Boolean(jsonSchemaElement.contact?.existingContacts),
    place: Boolean(jsonSchemaElement.contact?.place),
    comment: Boolean(jsonSchemaElement.contact?.comment),
  };
  const sourceProperty = jsonSchemaElement.contact?.existingContacts?.sourceProperty;
  const lookup = context?.contacts?.[uiSchemaElement.scope];
  const watchedSourceValue = useWatch({ control, name: sourceProperty as Path<T>, disabled: !sourceProperty });
  const sourceValue = sourceProperty ? watchedSourceValue : undefined;
  const isEnabled = (!modules.existingContacts || Boolean(sourceValue)) && !uiSchemaElement.disabled;

  const { field } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: undefined as PathValue<T, Path<T>>,
  });

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"select" | "create">("create");
  const [validateOnOpen, setValidateOnOpen] = useState(false);
  const [options, setOptions] = useState<TContactValue[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lookup || !sourceValue) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    lookup
      .fetch(sourceValue)
      .then((contacts) => {
        if (!cancelled) {
          setOptions(contacts.map((contact) => pickContactFields(contact as TContactValue, modules)));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      lookup.cancel?.();
    };
  }, [lookup, sourceValue]);

  useEffect(() => {
    if (!open) {
      setValidateOnOpen(false);
    }
  }, [open]);

  const { submitCount } = useFormState({ control });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to a new submit attempt on the outer form
  useEffect(() => {
    if (submitCount > 0 && field.value && !getContactSchema(modules.place).isValidSync(field.value)) {
      setMode("create");
      setOpen(true);
      setValidateOnOpen(true);
    }
  }, [submitCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only switch mode when the contacts list first resolves
  useEffect(() => {
    if (options.length > 0 && !field.value) {
      setMode("select");
    }
  }, [options.length]);

  const selected = field.value as TContactValue | null | undefined;
  const createDefaultValues = selected?._id ? {} : (selected ?? {});

  const contactForm = (
    <ContactForm
      key={selected?._id ?? "new"}
      place={modules.place}
      comment={modules.comment}
      defaultValues={createDefaultValues}
      validateOnMount={validateOnOpen}
      onConfirm={(data) => {
        field.onChange(pickContactFields(data, modules));
        setOpen(false);
      }}
      onCancel={() => setOpen(false)}
    />
  );

  return (
    <PaperCollapse
      open={open}
      onOpen={setOpen}
      ButtonProps={{ elevation: 2 }}
      actionButtons={
        selected &&
        !uiSchemaElement.disabled && (
          <>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
            >
              <EditIcon color="primary" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                field.onChange(null);
                setOpen(false);
              }}
            >
              <DeleteIcon color="primary" />
            </IconButton>
          </>
        )
      }
      title={
        selected ? (
          <Typography variant="body1SemiBold" color="primary">
            {getContactLabel(selected)}
          </Typography>
        ) : (
          <Typography variant="body1SemiBold" color="primary">
            {uiSchemaElement.label || "Contact"}
          </Typography>
        )
      }
      openIcon={!selected ? { open: <AddIcon />, close: <CloseIcon /> } : undefined}
      category={{
        icon: <ContactsIcon htmlColor="white" />,
        iconBackgroundColor: (theme) => (isEnabled ? theme.palette.success.main : theme.palette.disabled.light),
      }}
      disabled={!isEnabled || !!selected}
    >
      {!modules.existingContacts ? (
        open && contactForm
      ) : (
        <Stack sx={{ alignItems: "stretch", gap: 1 }}>
          {options.length > 0 ? (
            <Typography variant="body1">
              {options.length} contact{options.length > 1 ? "s existent" : " existe"} déjà. Sélectionnez-
              {options.length > 1 ? "en un" : "le"} ou créez-en un nouveau :
            </Typography>
          ) : (
            <Typography variant="body1">Aucun contact existant. Créez-en un nouveau :</Typography>
          )}
          <FormControl>
            <RadioGroup value={mode} onChange={(_, value) => setMode(value as "select" | "create")}>
              <FormControlLabel
                value="select"
                control={<Radio />}
                disabled={options.length === 0}
                label={
                  <Typography variant="body1SemiBold" color="primary">
                    Contact(s) existant(s)
                  </Typography>
                }
              />
              {mode === "select" && (
                <Autocomplete
                  size="small"
                  fullWidth
                  options={options}
                  loading={loading}
                  value={selected?._id ? selected : null}
                  onChange={(_, value) => {
                    field.onChange(value);
                    setOpen(false);
                  }}
                  getOptionLabel={getContactLabel}
                  isOptionEqualToValue={(option, value) => option._id === value?._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sélectionner un contact"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
              <FormControlLabel
                value="create"
                control={<Radio />}
                label={
                  <Typography variant="body1SemiBold" color="primary">
                    Nouveau contact
                  </Typography>
                }
              />
              {mode === "create" && open && contactForm}
            </RadioGroup>
          </FormControl>
        </Stack>
      )}
    </PaperCollapse>
  );
};
